import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Grid,
  Snackbar,
  Typography,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { captureException, captureMessage } from "@sentry/react";
import _ from "lodash";
import { useFinanceableTheme, styled } from "@financeable-com-au/financeable-ui";
import { useCloneApplication, useDemoCommercialApplication } from "@/hooks";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import { useFeatureFlags } from "@/utils/featureFlags/useFeatureFlags";
import { SubmissionModal } from "@/components/SubmissionModal";
import { LodgeAIModal } from "@/components/LodgeAIModal";
import { SidebarNavigation } from "@/components/SidebarNavigation";
import { reusableFetch } from "@/utils/useFetch";
import { createApplicationSocket } from "@/utils/applicationSocket";
import ActivityLogSidebar from "./ActivityLogSidebar";
import ServiceLogSidebar from "./ServiceLogSidebar";
import Comments from "./Comments";
import EditorNotes from "./EditorNotes";
import { ApplicationForm } from "./ApplicationForm";
import { ApplicationFormAlternativeLayout } from "./ApplicationFormAlternativeLayout";
import ApplicationMenu from "./ApplicationMenu";
import { DemoApplicationForm } from "./DemoApplicationForm";
import ApplicationFollowersModal from "./ApplicationFollowersModal";
import {
  lenderApi,
  useGetCommercialLendersQuery,
  useGetBusinessLendersQuery,
  useGetConsumerLendersQuery,
  useGetPersonalLendersQuery,
} from "../../store/slices/apiSlice";
import PageLoader from "../Application/PageLoader";
import {
  fieldRequiredMessage,
  fieldEntityRequiredMessage,
  applicationTypeList,
  getIsConsumerOrPersonalApplication,
  APPLICATION_STATUS,
  ERRORS_MESSAGE,
  SUCCESS_MESSAGE,
  userFilter,
  userType,
  DOCUMENT_TEMPLATE_TYPE,
  topbarHeight,
} from "../../constants";
import Sidebar from "../HOC/Sidebar";
import ProductSelector from "../ProductSelector/index";
import {
  saveValue,
  getApplication,
  applicationSelector,
  userDetailsSelector,
  clearState,
  getApplicationActivityLog,
  activityLogSelector,
  getApplicationServiceLog,
  // setRequiredFieldsError,
  setRequiredFieldsErrors,
  setValidationRequireFieldError,
  getApplicationComments,
  applicationCommentsSelector,
  setNewCommercialCreditFile,
  setNewCustomerCreditFile,
  setNewCustomerBankStatementFile,
  setNewDocumentFile,
  setNewAssetPPSRFile,
  setNewActivityLog,
  setNewServiceUsageLog,
  updateApplicationStatus,
  updateApplicationType,
  transferApplicationOwnership,
  sendDocumentSignInLink,
} from "../../store/slices/applicationFormSlice";
import regex from "../../utils/regex";
import { getAllUsers, userPermissionsSelector, userSelector } from "../../store/slices/userSlice";
import LoanSummary from "../LoanSummary";
import { ManualQuote } from "../ManualQuote";
import FormSelectField from "../CommonComponents/FormSelectField";
import { openSnackbar } from "../../store/slices/snackbarSlice";
import { getItem, removeLenderFilterItems } from "../../utils/localstorage";
import { ApplicationTypeUpdateAdditionalDetails } from "../Utils/ModalAdditionalDetails";
import ConfirmModal from "../customComponents/ConfirmModal";
import OwnershipUsersModel from "../Models/OwnershipUsersModel";
import { PreviewDocument } from "../Previews/PreviewDocument";
import RequestDocumentsModal from "../CustomerDetails/RequestDocumentsModal";

function openInNewTab(url) {
  var win = window?.open(url, "_blank");
  win?.focus();
}

/**
 * Styles.
 */
const SidebarNavigationContainer = styled(Box)`
  flex: 0 0 auto;
  position: sticky;
  top: ${topbarHeight};
  align-self: flex-start;
  height: calc(100vh - ${topbarHeight});
  z-index: 1;
`;

const Application = React.memo(() => {
  const theme = useFinanceableTheme();
  // Feature flags.
  const { isApplicationFormAlternativeLayout, isFUISidebarNav } = useFeatureFlags();
  const { isDemoCommercialApplication } = useDemoCommercialApplication();
  const { cloneApplication } = useCloneApplication();
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const permissions = useSelector(userPermissionsSelector);
  const { users, gettingUsers } = useSelector(userSelector);
  function hasTrueValue(obj) {
    return Object.values(obj).includes(true);
  }
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [openActivity, setOpenActivity] = useState(false);
  const [openServiceLog, setOpenServiceLog] = useState(false);
  const [openNotes, setOpenNotes] = useState(false);
  const [_isApplicationComplete, setIsApplicationComplete] = useState(false);
  const [applicationTypeUpdateModalOpen, setApplicationTypeUpdateModalOpen] = useState({
    isOpen: false,
    newApplicationType: "",
  });
  const [headerActionMenuAnchor, setHeaderActionMenuAnchor] = useState(null);

  const {
    linkSent,
    gotASICData,
    applicationServiceLog,
    applicationServiceLogLoader,
    openManualQuote,
    requireFieldErrors,
    validationRequireFieldError,
    openAssignOwnershipModal,
    isApplicationMenuOpen: isApplicationMenuOpenFromSelector,
    isLoanSummaryOpen,
    isCommentSectionOpen,
    isOpenFollowersModal,
    showPreviewDocument,
    documentPreview,
    documentLinkSentLoader,
    requestDocumentsModal,
  } = useSelector(userDetailsSelector);
  const application = useSelector(applicationSelector);
  const isApplicationOwner = !!(permissions?.userId && application?.user?._id === permissions.userId);
  const canUpdateFollowers = isApplicationOwner && permissions?.application?.followers_edit === true;
  const canUpdateApplicationOwnership =
    (isApplicationOwner && permissions?.application?.transfer_ownership === true) ||
    ([userType.owner, userType.master].includes(permissions?.userType) &&
      permissions?.application?.transfer_ownership === true &&
      ["all", "own"].includes(permissions?.application?.access));
  const requestDocumentsCustomer = application?.customers?.find(
    (customer) => customer?._id === requestDocumentsModal?.customerId,
  );
  const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(application.applicationType);
  const isReferrerUser = isDemoCommercialApplication ? true : !!permissions?.application?.internal_submit;

  const filteredApplicationTypeList = isDemoCommercialApplication
    ? applicationTypeList
    : applicationTypeList.filter((item) => item.label?.toLowerCase() !== ApplicationType.Commercial);

  const hideLenderSidebar = isReferrerUser;
  const isLenderSidebarVisible = permissions?.lenders && hasTrueValue(permissions?.lenders) && !hideLenderSidebar;
  const comments = useSelector(applicationCommentsSelector);
  // Overwrite sidebar with new FUI component.
  const showFUISidebarNav = isFUISidebarNav();
  const isApplicationMenuOpen = showFUISidebarNav ? false : isApplicationMenuOpenFromSelector;
  const isDisableApplicationEdit = !permissions?.application?.edit || !application?.canEditApplication;

  let {
    url,
    open,
    gettingApplication,
    trasferingApplicationOwnership,
    gettingApplicationComment,
    gettingClonedApplication,
    updatingApplicationStatus,
    updatingApplicationType,
  } = useSelector(userDetailsSelector);

  const activityLog = useSelector(activityLogSelector);

  const [assetFieldErrors, setAssetFieldErrors] = useState({
    assetValue: "",
    typeOfSale: "",
    condition: "",
    make: "",
    model: "",
    glassesMake: "",
    glassesModel: "",
    glassesVariant: "",
    glassesNVIC: "",
    glassesData: "",
    registrationNumberOrVin: "",
  });

  const [guarantorFieldErrors, setGuarantorFieldErrors] = useState({
    customerTitle: "",
    firstName: "",
    lastName: "",
    IDNumber: "",
    dateOfBirth: "",
    IDCardNumber: "",
    phoneNumber: "",
    emailAddress: "",
    address: "",
    yearsAt: "",
    monthsAt: "",
    IDExpiryDate: "",
  });

  const [guarantorsError, setGuarantorsError] = useState([]);

  const [agreementsFieldErrors, setAgreementsFieldErrors] = useState({
    introducerDeclarationAgree: "",
  });

  const [nccpFieldErrors, setNccpFieldErrors] = useState({
    whatIsThePurposeOfThisApplication: "", // Enum
    whyDoesThisLoanTermSuitYourRequirements: "", // Enum
    whatAreYouLookingForInYourLoan: "", // Enum
    anyForeseeableChangesImpactingLoanRepayment: "", // Enum
    howApplicantIntendToDoLoanRepaymentAfterChange: "", // Textarea
    whyIsThisLoanNotUnsuitableForTheApplicantDetail: "", // Textarea
    increaseInLoanAmountForFinancingCostOfInsurance: "", // boolean
    balloonPaymentAtTheEndOfTheLoan: "", // boolean
    whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm: "", // Enum
  });

  const [entityFieldErrors, setEntityFieldErrors] = useState({
    address: "",
  });

  const [socket, setSocket] = useState(null);
  const [currentRoom, setCurrentRoom] = useState(null);

  useEffect(() => {
    if (!socket) {
      setSocket(createApplicationSocket("application"));
    }

    return () => {
      socket?.disconnect();
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
    let active = true;

    if (applicationId) {
      const joinApplicationRoom = () => {
        socket.emit("joinApplicationRoom", applicationId, (result) => {
          if (!active) return;
          if (result?.ok) {
            setCurrentRoom(applicationId);
            return;
          }
          captureMessage(`Socket.IO application room join failed: ${result?.error || "no acknowledgement"}`);
        });
      };

      if (currentRoom && currentRoom !== applicationId) {
        socket.emit("leaveApplicationRoom", currentRoom, (result) => {
          if (result?.ok) {
            joinApplicationRoom();
          }
        });
      } else if (!currentRoom) {
        joinApplicationRoom();
      }

      const handleNewCommercialCreditFile = (commercialCreditFile) => {
        dispatch(setNewCommercialCreditFile(commercialCreditFile));
      };

      const handleNewCustomerCreditFile = (customerCreditFile) => {
        dispatch(setNewCustomerCreditFile(customerCreditFile));
      };

      const handleChatOptionEnable = (chatOptions) => {
        dispatch(
          saveValue({
            isIntercomChatOptions: chatOptions,
          }),
        );
      };

      const handleNewBankStatementFile = (bankStatementFile) => {
        dispatch(setNewCustomerBankStatementFile(bankStatementFile));
      };

      const handleNewDocumentFile = (customerPrivacyFile) => {
        // Generic handler for uploads that uses document file upload callback endpoint
        // e.g privacy policy file uploads, identity verification file uploads
        dispatch(setNewDocumentFile(customerPrivacyFile));
      };

      const handleNewAssetPPSRFile = (assetPPSRData) => {
        const assetId = assetPPSRData.asset;
        const certificateDocument = assetPPSRData.certificateDocument;
        dispatch(setNewAssetPPSRFile({ assetId, certificateDocument }));
      };

      const handleNewActivityLog = (activityLog) => {
        dispatch(setNewActivityLog(activityLog));
      };

      const handleNewServiceUsageLog = (serviceUsageLog) => {
        dispatch(setNewServiceUsageLog(serviceUsageLog));
      };

      const handleConnect = () => {
        joinApplicationRoom();
      };

      socket.on("commercialCreditFileUploaded", handleNewCommercialCreditFile);
      socket.on("customerCreditFileUploaded", handleNewCustomerCreditFile);
      socket.on("intercomChatNotification", handleChatOptionEnable);
      socket.on("bankStatementFileUploaded", handleNewBankStatementFile);
      socket.on("documentFileUploaded", handleNewDocumentFile);
      socket.on("assetPPSRFileUploaded", handleNewAssetPPSRFile);
      socket.on("newActivityLog", handleNewActivityLog);
      socket.on("newServiceUsageLog", handleNewServiceUsageLog);
      socket.on("connect", handleConnect);

      return () => {
        active = false;
        // Cleanup functions
        socket.emit("leaveApplicationRoom", currentRoom);
        socket.off("commercialCreditFileUploaded", handleNewCommercialCreditFile);
        socket.off("customerCreditFileUploaded", handleNewCustomerCreditFile);
        socket.off("intercomChatNotification", handleChatOptionEnable);
        socket.off("bankStatementFileUploaded", handleNewBankStatementFile);
        socket.off("documentFileUploaded", handleNewDocumentFile);
        socket.off("assetPPSRFileUploaded", handleNewAssetPPSRFile);
        socket.off("newActivityLog", handleNewActivityLog);
        socket.off("newServiceUsageLog", handleNewServiceUsageLog);
        socket.off("connect", handleConnect);
      };
    }
  }, [applicationId, currentRoom, socket]);

  useEffect(() => {
    if (openActivity && applicationId) {
      dispatch(getApplicationActivityLog(applicationId));
    }
    if (!isLenderSidebarVisible) {
      setOpenNotes(true);
    }
  }, [application?.customers, application?.asset, application?.entities, application?.loanDetails]);

  useEffect(() => {
    if (applicationId) {
      dispatch(getApplicationActivityLog(applicationId));
    }
  }, []);

  const handle = {
    cloneApplication: () => cloneApplication(applicationId),
    toggleApplicationMenu: () => {
      dispatch(saveValue({ isApplicationMenuOpen: !isApplicationMenuOpen }));
    },
    toggleLoanSummary: () => {
      dispatch(saveValue({ isLoanSummaryOpen: !isLoanSummaryOpen }));
    },
    toggleComments: () => {
      dispatch(saveValue({ isCommentSectionOpen: !isCommentSectionOpen, isLoanSummaryOpen: false }));
      if (!isCommentSectionOpen === true && applicationId) {
        dispatch(getApplicationComments(applicationId));
      }
      if (!isLenderSidebarVisible) {
        setOpenNotes(isCommentSectionOpen);
      }
    },
    openAssignOwnershipModal: () => {
      setHeaderActionMenuAnchor(null);
      dispatch(saveValue({ openAssignOwnershipModal: true }));
      dispatch(getAllUsers({ cursor: 1, limit: 1000, search: "", filter: userFilter.ACTIVE }));
    },
    closeAssignOwnershipModal: () => {
      dispatch(saveValue({ openAssignOwnershipModal: false }));
    },
    transferOwnership: (userId) => {
      dispatch(transferApplicationOwnership({ applicationId, ...(userId ? { userId } : {}) })).then((res) => {
        setHeaderActionMenuAnchor(null);
        dispatch(saveValue({ openAssignOwnershipModal: false }));
        if (res?.error) {
          dispatch(
            openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.applicationOwnershipTransferredSuccessMsg,
              intent: "success",
            }),
          );
        }
      });
    },
    handleApplicationComplete: (isComplete) => {
      setIsApplicationComplete(isComplete);
    },
    activityLog: () => {
      const extractKey = [
        "addresses",
        "customerAssets",
        "documents",
        "emails",
        "employers",
        "expenses",
        "income",
        "liabilities",
        "phoneNumbers",
      ];

      const list = _.flatMap(Object.keys(application), (item) => {
        if (["asset", "loanDetails"].includes(item)) {
          return [{ type: item, ...application[item] }];
        }

        if (["customers", "entities"].includes(item)) {
          return _.flatMap(application[item], (cusEnt) => {
            const result = [{ type: item, ...cusEnt }];
            _.forEach(cusEnt, (value, key) => {
              if (extractKey.includes(key) && value.createdAt) {
                result.push({ type: key, ...value });
              }
            });
            return result;
          });
        }
        return [];
      });

      return list;
    },
    applicationLoad: async () => {
      dispatch(saveValue({ gettingApplication: true, open: false }));
      if (applicationId) await dispatch(getApplication(applicationId)).unwrap();
    },
    applicationTitle: (applicationType) => {
      return applicationTypeList.find((item) => item.value === applicationType)?.label;
    },
    statusUpdate: (status) => {
      dispatch(updateApplicationStatus({ applicationId, status })).then((res) => {
        if (res?.error) {
          dispatch(
            openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.applicationStatusUpdatedSuccessMsg,
              intent: "success",
            }),
          );
        }
      });
    },
    manageApplicationTypeUpdateModal: (isOpen, newApplicationType) => {
      setApplicationTypeUpdateModalOpen({
        isOpen,
        newApplicationType: newApplicationType || "",
      });
    },
    applicationTypeUpdate: (data) => {
      if (!data?.isSave || !applicationTypeUpdateModalOpen?.newApplicationType) {
        handle.manageApplicationTypeUpdateModal(false, "");
        return;
      }
      // remove localstorage lender filter items
      removeLenderFilterItems({ applicationId, fieldKey: "filterBy" });

      dispatch(
        updateApplicationType({ applicationId, applicationType: applicationTypeUpdateModalOpen?.newApplicationType }),
      ).then((res) => {
        handle.manageApplicationTypeUpdateModal(false, "");
        if (res?.error) {
          dispatch(
            openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.applicationTypeUpdatedSuccessMsg,
              intent: "success",
            }),
          );
        }
      });
    },
    showRequired: (currentRequiredFields) => {
      const personalDetailError = {};
      currentRequiredFields?.customer?.forEach((field) => {
        const fieldValue = field.split("_")[0];
        // const filterField = _.startCase(fieldValue?.split(regex.startCaseRegex)[1]);
        const fieldEndVal = fieldValue?.split(regex.startCaseRegex)[1];

        if (field.includes("customerAssets")) {
          const endField = field.split("customerAssets.[0].")[1] ?? field.split("customerAssets[0].")[1];
          personalDetailError[endField] = fieldRequiredMessage.customerAssetsErrMsg[fieldEndVal];
        } else if (field.includes("liabilities")) {
          const endField = field.split("liabilities.[0].")[1] ?? field.split("liabilities[0].")[1];
          if (endField.includes("description")) {
            personalDetailError[`liability_${endField}`] = fieldRequiredMessage.liabilityErrMsg[fieldEndVal];
          } else {
            personalDetailError[endField] = fieldRequiredMessage.liabilityErrMsg[fieldEndVal];
          }
        } else if (field.includes("income")) {
          const endField = field.split("income.[0].")[1] ?? field.split("income[0].")[1];
          personalDetailError[endField] = fieldRequiredMessage.incomeErrMsg[fieldEndVal];
        } else if (field.includes("expenses")) {
          const endField = field.split("expenses.[0].")[1] ?? field.split("expenses[0].")[1];
          personalDetailError[endField] = fieldRequiredMessage.expenseErrMsg[fieldEndVal];
        } else if (field.includes("employers")) {
          const endField = field.split("employers.[0].")[1];
          const endFieldKey =
            endField && endField !== undefined && endField.includes("address")
              ? endField.split("address.")[1]
              : endField;
          if (endFieldKey && endFieldKey.includes("custom") && !endFieldKey.includes("customerTitle")) {
            const endCustomFieldKey = endFieldKey.replace("_customError", "");
            personalDetailError[endCustomFieldKey] = fieldRequiredMessage.customErrMsg[fieldEndVal];
          } else {
            personalDetailError[endFieldKey] = fieldRequiredMessage.employeeErrMsg[fieldEndVal];
          }
        } else if (fieldEndVal === undefined) {
          if (fieldValue === "customerRequired") {
            personalDetailError[field] = isConsumerOrPersonalApplication
              ? fieldRequiredMessage.customErrMsg.borrowerRequired
              : fieldRequiredMessage.customErrMsg.guarantorRequired;
          } else if (field.includes("custom") && !field.includes("customerTitle")) {
            const customFieldKey = field.replace("_customError", "");
            personalDetailError[customFieldKey] = fieldRequiredMessage.customErrMsg[fieldValue];
          } else {
            personalDetailError[field] = fieldRequiredMessage[fieldValue];
          }
        }
        setGuarantorsError(personalDetailError);
      });
      dispatch(
        setRequiredFieldsErrors({
          ...requireFieldErrors,
          ...personalDetailError,
        }),
      );
      const assetsFieldError = {};
      currentRequiredFields?.asset?.forEach((field) => {
        assetsFieldError[field] = fieldRequiredMessage[field];
      });

      const agreementFieldError = {};
      currentRequiredFields?.agreement?.forEach((field) => {
        agreementFieldError[field] = fieldRequiredMessage[field];
      });

      const entityFieldError = {};
      currentRequiredFields?.entity?.forEach((field) => {
        const fieldValue = field.split("_")[0];
        entityFieldError[field] = fieldEntityRequiredMessage[fieldValue];
      });

      setEntityFieldErrors({
        ...entityFieldErrors,
        ...entityFieldError,
      });
      dispatch(
        setValidationRequireFieldError({
          ...validationRequireFieldError,
          agreementError: {
            ...validationRequireFieldError?.agreementError,
            ...agreementFieldError,
          },
          assetErrors: {
            ...validationRequireFieldError?.assetErrors,
            ...assetsFieldError,
          },
          entityFieldErrors: {
            ...validationRequireFieldError?.entityFieldErrors,
            ...entityFieldError,
          },
          customerErrors: {
            ...validationRequireFieldError?.customerErrors,
            ...personalDetailError,
          },
        }),
      );
    },
    closePreviewDocumentModal: () => {
      dispatch(
        saveValue({
          showPreviewDocument: {
            isOpen: false,
            customerId: "",
            templateType: "",
            disclosureInfo: null,
          },
          documentPreview: null,
          privacyLoading: false,
          disclosureLoader: false,
        }),
      );
    },
    sendDocumentSignInLink: () => {
      const sendDocumentSignInLinkPayload = {
        applicationId,
        customerId: showPreviewDocument?.customerId,
        templateType: showPreviewDocument?.templateType,
        ...(showPreviewDocument?.templateType === DOCUMENT_TEMPLATE_TYPE.CUSTOMER_DISCLOSURE &&
        showPreviewDocument?.disclosureInfo
          ? { disclosureInfo: showPreviewDocument.disclosureInfo }
          : {}),
      };
      dispatch(sendDocumentSignInLink(sendDocumentSignInLinkPayload)).then((res) => {
        if (res.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.documentLinkSent,
              intent: "success",
            }),
          );
        }
      });
    },
  };

  useEffect(() => {
    handle.applicationLoad();
    return function cleanUp() {
      dispatch(clearState());
    };
  }, [applicationId]);

  useEffect(() => {
    if (url) openInNewTab(url);
  }, [url]);

  const [skipConsumer, setSkipConsumer] = useState(true);
  const [skipPersonal, setSkipPersonal] = useState(true);
  const [skipCommercial, setSkipCommercial] = useState(true);
  const [skipBusiness, setSkipBusiness] = useState(true);

  const consumerResult = useGetConsumerLendersQuery({}, { skip: skipConsumer });
  const personalResult = useGetPersonalLendersQuery({}, { skip: skipPersonal });
  const commercialResult = useGetCommercialLendersQuery({}, { skip: skipCommercial });
  const businessResult = useGetBusinessLendersQuery({}, { skip: skipBusiness });

  useEffect(() => {
    switch (application.applicationType) {
      case ApplicationType.Consumer:
        setSkipConsumer(false);
        setSkipPersonal(true);
        setSkipCommercial(true);
        setSkipBusiness(true);
        break;
      case ApplicationType.Personal:
        setSkipConsumer(true);
        setSkipPersonal(false);
        setSkipCommercial(true);
        setSkipBusiness(true);
        break;
      case ApplicationType.Commercial:
        setSkipConsumer(true);
        setSkipPersonal(true);
        setSkipCommercial(false);
        setSkipBusiness(true);
        break;
      case ApplicationType.Business:
        setSkipConsumer(true);
        setSkipPersonal(true);
        setSkipCommercial(true);
        setSkipBusiness(false);
        break;
      default:
        setSkipConsumer(true);
        setSkipPersonal(true);
        setSkipCommercial(true);
        setSkipBusiness(true);
        break;
    }
  }, [application.applicationType]);

  // Determine which result to use based on applicationType
  const lenderResultByType = {
    [ApplicationType.Consumer]: consumerResult,
    [ApplicationType.Personal]: personalResult,
    [ApplicationType.Commercial]: commercialResult,
    [ApplicationType.Business]: businessResult,
  };
  const defaultLenderResult = { data: null, error: null, isLoading: false };
  const result = lenderResultByType[application?.applicationType] ?? defaultLenderResult;
  const { data: lenderData, isFetching } = result;

  useEffect(() => {
    const request = window.indexedDB.open("financeableDB");
    let queries;
    request.onsuccess = async () => {
      const db = request.result;
      const objectStore = db.transaction(db.objectStoreNames[0], "readonly").objectStore("keyvaluepairs");
      const objectStoreData = objectStore.get("persist:root");

      objectStoreData.onsuccess = () => {
        if (objectStoreData.result) {
          const data = JSON.parse(objectStoreData.result ?? "");
          const lenderApiData = JSON.parse(data.lenderApi ?? "");
          queries = lenderApiData.queries;
          Object.keys(queries ?? {}).forEach(async (query) => {
            const queryTimestamp = Math.floor(queries[query].fulfilledTimeStamp / 1000) * 1000;
            if (["pending", "rejected"].includes(queries[query].status) || (await logTimestamp()) > queryTimestamp) {
              switch (application.applicationType) {
                case ApplicationType.Consumer:
                  dispatch(
                    lenderApi.endpoints.getConsumerLenders.initiate({}, { subscribe: true, forceRefetch: true }),
                  );
                  break;
                case ApplicationType.Personal:
                  dispatch(
                    lenderApi.endpoints.getPersonalLenders.initiate({}, { subscribe: true, forceRefetch: true }),
                  );
                  break;
                case ApplicationType.Commercial:
                  dispatch(
                    lenderApi.endpoints.getCommercialLenders.initiate({}, { subscribe: true, forceRefetch: true }),
                  );
                  break;
                case ApplicationType.Business:
                  dispatch(
                    lenderApi.endpoints.getBusinessLenders.initiate({}, { subscribe: true, forceRefetch: true }),
                  );
                  break;
                default:
                  break;
              }
            }
          });
        }
      };
    };

    request.onerror = (error) => {
      captureMessage("Error connecting to IndexedDB");
      captureException(error);
    };

    const logTimestamp = async () => {
      const { data } = await reusableFetch(`lender/log`, "GET", null);
      const lenderLog = data.data.lenderLog;
      const logTimestamp = Math.floor((new Date(lenderLog?.createdAt) || 0) / 1000) * 1000;
      return logTimestamp;
    };
  }, [application.applicationType]);

  const applicationTitleString = isDemoCommercialApplication
    ? "Commercial"
    : handle.applicationTitle(application.applicationType);
  const isProductLenderFiltersExist = getItem("productLenderFilters"); // get item from local storage
  const isLenderFiltersExist = isProductLenderFiltersExist
    ? JSON.parse(isProductLenderFiltersExist)?.[applicationId]
    : false;

  const archivedOrDeleted = ["Archived", "Deleted"];

  return (
    <>
      {gettingApplication ? (
        <Grid
          container
          style={{
            marginTop: "64px",
            padding: "30px 0px 100px",
            height: "calc(100vh - 100px)",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <PageLoader text="Application loading..." />
        </Grid>
      ) : (
        <Grid
          container
          item
          xs={12}
          md={9}
          lg={9}
          xl={showFUISidebarNav ? 9 : isApplicationMenuOpen ? 10 : 11}
          padding={
            showFUISidebarNav
              ? {
                  xs: 0,
                  sm: "0 20px 0 0",
                  lg: "0 20px 0 0",
                  xl: "0 20px 0 0",
                }
              : isApplicationMenuOpen
                ? {
                    xs: 0,
                    sm: "20px 40px 20px 40px",
                    md: "20px 0 20px 180px",
                    lg: "10px 20px 20px 250px",
                    xl: "10px 120px 20px 250px",
                  }
                : {
                    xs: 0,
                    sm: "20px 40px 20px 40px",
                    lg: "10px 110px 20px 0px",
                    xl: "10px 350px 20px 0px",
                  }
          }
        >
          {application?._id && (
            <>
              <Grid container item wrap="nowrap">
                {showFUISidebarNav && (
                  <SidebarNavigationContainer>
                    <SidebarNavigation />
                  </SidebarNavigationContainer>
                )}
                <Grid
                  container
                  item
                  sx={
                    showFUISidebarNav
                      ? {
                          flex: "1 1 auto",
                          minWidth: 0,
                        }
                      : undefined
                  }
                  paddingY={showFUISidebarNav ? 4 : undefined}
                  paddingX={showFUISidebarNav ? 4 : undefined}
                  minHeight={showFUISidebarNav ? "100vh" : undefined}
                  alignItems={showFUISidebarNav ? "flex-start" : undefined}
                  alignContent={showFUISidebarNav ? "flex-start" : undefined}
                >
                  <Grid item xs={12} md={5.5}>
                    <Typography
                      variant="h3"
                      style={{
                        fontSize: "22px",
                        margin: "0 0 30px",
                      }}
                    >
                      {applicationTitleString} Application {application?.humanId && `- ${application?.humanId}`}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    gap={1}
                    xs={12}
                    md={6.5}
                    sx={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "start",
                      margin: "0 0 30px",
                    }}
                  >
                    {!isDemoCommercialApplication && (
                      <FormSelectField
                        fullWidth
                        fieldName="applicationType"
                        label="Application Type"
                        value={application?.applicationType}
                        options={filteredApplicationTypeList}
                        onChange={(applicationType) => handle.manageApplicationTypeUpdateModal(true, applicationType)}
                        disabled={updatingApplicationType || isDisableApplicationEdit}
                        gridProps={{ md: 4, sm: 6, xs: 12 }}
                      />
                    )}

                    <FormSelectField
                      fullWidth
                      fieldName="status"
                      label="Application Status"
                      value={application?.status}
                      options={
                        archivedOrDeleted.includes(application?.status)
                          ? [...APPLICATION_STATUS, { label: application?.status, value: application?.status }]
                          : APPLICATION_STATUS
                      }
                      onChange={handle.statusUpdate}
                      disabled={updatingApplicationStatus || archivedOrDeleted.includes(application?.status)}
                      gridProps={{ md: 4, sm: 6, xs: 12 }}
                    />

                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ height: "40px" }}
                      disabled={gettingClonedApplication || isDisableApplicationEdit}
                      onClick={handle.cloneApplication}
                    >
                      {gettingClonedApplication ? "Cloning..." : "Clone Application"}
                    </Button>
                    {(canUpdateFollowers || canUpdateApplicationOwnership) && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => setHeaderActionMenuAnchor(e.currentTarget)}
                          sx={{ height: "40px", width: "40px" }}
                          aria-label="Application actions menu"
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={headerActionMenuAnchor}
                          open={Boolean(headerActionMenuAnchor)}
                          onClose={() => setHeaderActionMenuAnchor(null)}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "right" }}
                          slotProps={{
                            paper: {
                              sx: {
                                mt: 1.2,
                                minWidth: 200,
                                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                                borderRadius: 2,
                                "& .MuiMenuItem-root": { px: 2, py: 1.5, fontSize: "0.875rem" },
                              },
                            },
                          }}
                        >
                          {canUpdateFollowers && (
                            <MenuItem
                              onClick={() => {
                                setHeaderActionMenuAnchor(null);
                                dispatch(saveValue({ isOpenFollowersModal: true }));
                              }}
                              sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                            >
                              <ListItemIcon>
                                <AssignmentIndIcon fontSize="small" sx={{ color: "primary.main" }} />
                              </ListItemIcon>
                              <ListItemText
                                primary="Manage followers"
                                primaryTypographyProps={{ fontWeight: 500, fontSize: "14px" }}
                              />
                            </MenuItem>
                          )}
                          {canUpdateApplicationOwnership && (
                            <>
                              <MenuItem
                                onClick={() => handle.transferOwnership(null)}
                                disabled={trasferingApplicationOwnership}
                                sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                              >
                                <ListItemIcon>
                                  <PersonAddIcon fontSize="small" sx={{ color: "primary.main" }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    trasferingApplicationOwnership ? "Transferring Ownership..." : "Take Ownership"
                                  }
                                  primaryTypographyProps={{ fontWeight: 500, fontSize: "14px" }}
                                />
                              </MenuItem>
                              {[userType.owner, userType.master].includes(permissions?.userType) && (
                                <MenuItem
                                  onClick={handle.openAssignOwnershipModal}
                                  disabled={gettingUsers}
                                  sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                                >
                                  <ListItemIcon>
                                    <PeopleIcon fontSize="small" sx={{ color: "primary.main" }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary="Assign Ownership"
                                    primaryTypographyProps={{ fontWeight: 500, fontSize: "14px" }}
                                  />
                                </MenuItem>
                              )}
                            </>
                          )}
                        </Menu>
                      </>
                    )}
                  </Grid>

                  {isDemoCommercialApplication && (
                    <DemoApplicationForm
                      assetFieldErrors={assetFieldErrors}
                      setAssetFieldErrors={setAssetFieldErrors}
                      guarantorFieldErrors={guarantorFieldErrors}
                      setGuarantorFieldErrors={setGuarantorFieldErrors}
                      guarantorsError={guarantorsError}
                      setGuarantorsError={setGuarantorsError}
                      entityFieldErrors={entityFieldErrors}
                      setEntityFieldErrors={setEntityFieldErrors}
                      agreementsFieldErrors={agreementsFieldErrors}
                      setAgreementsFieldErrors={setAgreementsFieldErrors}
                      nccpFieldErrors={nccpFieldErrors}
                      setNccpFieldErrors={setNccpFieldErrors}
                    />
                  )}

                  {!isDemoCommercialApplication && isApplicationFormAlternativeLayout() && (
                    <ApplicationFormAlternativeLayout
                      assetFieldErrors={assetFieldErrors}
                      setAssetFieldErrors={setAssetFieldErrors}
                      guarantorFieldErrors={guarantorFieldErrors}
                      setGuarantorFieldErrors={setGuarantorFieldErrors}
                      guarantorsError={guarantorsError}
                      setGuarantorsError={setGuarantorsError}
                      entityFieldErrors={entityFieldErrors}
                      setEntityFieldErrors={setEntityFieldErrors}
                      agreementsFieldErrors={agreementsFieldErrors}
                      setAgreementsFieldErrors={setAgreementsFieldErrors}
                      nccpFieldErrors={nccpFieldErrors}
                      setNccpFieldErrors={setNccpFieldErrors}
                    />
                  )}

                  {!isDemoCommercialApplication && !isApplicationFormAlternativeLayout() && (
                    <ApplicationForm
                      assetFieldErrors={assetFieldErrors}
                      setAssetFieldErrors={setAssetFieldErrors}
                      guarantorFieldErrors={guarantorFieldErrors}
                      setGuarantorFieldErrors={setGuarantorFieldErrors}
                      guarantorsError={guarantorsError}
                      setGuarantorsError={setGuarantorsError}
                      entityFieldErrors={entityFieldErrors}
                      setEntityFieldErrors={setEntityFieldErrors}
                      agreementsFieldErrors={agreementsFieldErrors}
                      setAgreementsFieldErrors={setAgreementsFieldErrors}
                      nccpFieldErrors={nccpFieldErrors}
                      setNccpFieldErrors={setNccpFieldErrors}
                    />
                  )}
                </Grid>
              </Grid>
              {isLenderSidebarVisible && (
                <Grid
                  container
                  item
                  display={{ xs: "none", md: "grid" }}
                  sm={3}
                  md={3}
                  lg={2}
                  padding={showFUISidebarNav ? 0 : "20px 20px 20px 0"}
                >
                  <Sidebar
                    itemPosition={0}
                    open={open}
                    title={"Expand lenders"}
                    onClickFn={() => {
                      dispatch(saveValue({ open: !open, openManualQuote: false }));
                      if (!open) {
                        dispatch(setValidationRequireFieldError({}));
                        dispatch(setRequiredFieldsErrors({}));
                      }
                    }}
                    bottom={""}
                    right={true}
                    left={false}
                    disabled={false}
                    forwardIcon={<ArrowForwardIosIcon fontSize={"large"} />}
                    backwardIcon={<ChecklistRtlIcon fontSize="large" />}
                    background={theme.palette.neutral[100]}
                    component={
                      !isFetching && lenderData ? (
                        <ProductSelector onShowRequired={handle.showRequired} lenderData={lenderData} />
                      ) : (
                        <PageLoader text={"Lenders Loading"} />
                      )
                    }
                  />
                  <Sidebar
                    itemPosition={1}
                    open={openManualQuote}
                    title={"Manual Quote"}
                    onClickFn={() => dispatch(saveValue({ openManualQuote: !openManualQuote }))}
                    bottom={""}
                    right={true}
                    left={false}
                    disabled={false}
                    background={theme.palette.neutral[50]}
                    forwardIcon={<CalculateOutlinedIcon fontSize={"large"} />}
                    backwardIcon={<CalculateOutlinedIcon fontSize="large" />}
                    component={<ManualQuote application={application} />}
                  />
                </Grid>
              )}
              {/* Sidebar Buttons */}
              <Box display={{ xs: "none", md: "block" }}>
                <Box display={{ xs: "none", md: "block" }}>
                  {/* New application sidebar menu */}
                  {!showFUISidebarNav && (
                    <Sidebar
                      itemPosition={0}
                      open={isApplicationMenuOpen}
                      title={"Application menu"}
                      onClickFn={() => {}}
                      bottom={""}
                      right={false}
                      left={true}
                      overflowY={"hidden"}
                      dispatch={false}
                      background={theme.palette.neutral[50]}
                      component={
                        <ApplicationMenu
                          isApplicationMenuOpen={isApplicationMenuOpen}
                          toggleApplicationMenu={handle.toggleApplicationMenu}
                        />
                      }
                    />
                  )}

                  {!isReferrerUser && (
                    <Sidebar
                      itemPosition={2}
                      open={openNotes}
                      title={"Notes"}
                      onClickFn={() => setOpenNotes(!openNotes)}
                      bottom={520}
                      right={true}
                      left={false}
                      dispatch={false}
                      icon={<EditNoteIcon fontSize="large" />}
                      background={theme.palette.neutral[50]}
                      component={<EditorNotes isDisableApplicationEdit={isDisableApplicationEdit} />}
                    />
                  )}

                  {isReferrerUser && (
                    <Sidebar
                      itemPosition={2} // 3 is the base index
                      open={isLoanSummaryOpen}
                      title={"Loan Summary"}
                      onClickFn={handle.toggleLoanSummary}
                      bottom={""}
                      right={true}
                      left={false}
                      dispatch={false}
                      icon={!isDemoCommercialApplication && <RequestQuoteOutlinedIcon fontSize="large" />}
                      background={theme.palette.neutral[50]}
                      component={<LoanSummary handleApplicationComplete={handle.handleApplicationComplete} />}
                    />
                  )}

                  {!isDemoCommercialApplication && (
                    <>
                      <Sidebar
                        itemPosition={3}
                        open={isCommentSectionOpen}
                        title={"Comments"}
                        onClickFn={handle.toggleComments}
                        bottom={450}
                        right={true}
                        left={false}
                        overflowY={"hidden"}
                        dispatch={false}
                        icon={<CommentOutlinedIcon fontSize="large" />}
                        background={theme.palette.neutral[50]}
                        component={
                          <Comments
                            comments={comments}
                            applicationId={applicationId}
                            gettingApplicationComment={gettingApplicationComment}
                            isDisableApplicationEdit={isDisableApplicationEdit}
                          />
                        }
                      />
                      <Sidebar
                        itemPosition={4}
                        open={openActivity}
                        title={"Activity log"}
                        onClickFn={() => {
                          setOpenActivity(!openActivity);
                          if (!openActivity === true && applicationId) {
                            dispatch(getApplicationActivityLog(applicationId));
                          }
                        }}
                        bottom={400}
                        right={true}
                        left={false}
                        disabled={false}
                        background={theme.palette.neutral[50]}
                        icon={<EventNoteOutlinedIcon fontSize="large" />}
                        component={<ActivityLogSidebar activityList={activityLog} openActivity={openActivity} />}
                      />
                      <Sidebar
                        itemPosition={5}
                        open={openServiceLog}
                        title={"Service log"}
                        onClickFn={() => {
                          setOpenServiceLog(!openServiceLog);
                          if (!openServiceLog === true && applicationId) {
                            dispatch(getApplicationServiceLog(applicationId));
                          }
                        }}
                        bottom={400}
                        right={true}
                        left={false}
                        disabled={false}
                        background={theme.palette.neutral[50]}
                        icon={<ViewHeadlineIcon fontSize="large" />}
                        component={
                          <ServiceLogSidebar serviceList={applicationServiceLog} loader={applicationServiceLogLoader} />
                        }
                      />
                    </>
                  )}
                </Box>
              </Box>
            </>
          )}

          <Snackbar
            open={submitSuccess}
            autoHideDuration={8000}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            onClose={() => setSubmitSuccess(false)}
          >
            <Alert onClose={() => setSubmitSuccess(false)} elevation={6} severity="success" sx={{ width: "100%" }}>
              Application submitted
            </Alert>
          </Snackbar>
          <Snackbar
            open={linkSent}
            autoHideDuration={8000}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            onClose={() => dispatch(saveValue({ linkSent: false }))}
          >
            <Alert
              onClose={() => dispatch(saveValue({ linkSent: false }))}
              elevation={6}
              severity="success"
              sx={{ width: "100%" }}
            >
              Link sent
            </Alert>
          </Snackbar>
          <Snackbar
            open={gotASICData}
            autoHideDuration={8000}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            onClose={() => dispatch(saveValue({ linkSent: false }))}
          >
            <Alert
              onClose={() => dispatch(saveValue({ linkSent: false }))}
              elevation={6}
              severity="success"
              sx={{ width: "100%" }}
            >
              ASIC Data retrieved
            </Alert>
          </Snackbar>
        </Grid>
      )}
      <SubmissionModal />
      <LodgeAIModal />
      {applicationTypeUpdateModalOpen?.isOpen && (
        <ConfirmModal
          isOpen={applicationTypeUpdateModalOpen?.isOpen}
          handle={handle.applicationTypeUpdate}
          setIsOpen={(isOpen) => handle.manageApplicationTypeUpdateModal(isOpen, "")}
          title={"Application Type Update"}
          btnTextYes="Update"
          additionalDetails={
            <ApplicationTypeUpdateAdditionalDetails
              newApplicationType={applicationTypeUpdateModalOpen?.newApplicationType}
              isProductQuotesExist={application?.productsQuotes?.length > 0}
              isLenderFiltersExist={isLenderFiltersExist}
            />
          }
          message={"Are you sure you want to update the application type?"}
          isConfirmLoading={updatingApplicationType}
          confirmLoadingText="Updating..."
        />
      )}
      {openAssignOwnershipModal && (
        <OwnershipUsersModel
          applicationId={applicationId}
          isLoading={gettingUsers}
          gettingUsers={gettingUsers}
          handleAssignOwnership={handle.transferOwnership}
          ownershipUsersList={users?.docs || []}
          open={openAssignOwnershipModal}
          isAssigningOwnership={trasferingApplicationOwnership}
          handleClose={handle.closeAssignOwnershipModal}
        />
      )}
      {isOpenFollowersModal && (
        <ApplicationFollowersModal
          open={isOpenFollowersModal}
          onClose={() => dispatch(saveValue({ isOpenFollowersModal: false }))}
          applicationId={applicationId}
        />
      )}
      {showPreviewDocument?.isOpen && (
        <PreviewDocument
          open={showPreviewDocument?.isOpen}
          previewBuffer={documentPreview}
          confirmationLoader={documentLinkSentLoader} // Need to add loader for confirmation
          onClose={handle.closePreviewDocumentModal}
          onConfirm={handle.sendDocumentSignInLink}
          templateType={showPreviewDocument?.templateType}
        />
      )}
      {requestDocumentsModal?.isOpen && requestDocumentsCustomer && (
        <RequestDocumentsModal
          open={requestDocumentsModal?.isOpen}
          onClose={() => dispatch(saveValue({ requestDocumentsModal: { isOpen: false, customerId: "" } }))}
          customer={requestDocumentsCustomer}
          applicationId={applicationId}
          applicationType={application?.applicationType}
        />
      )}
    </>
  );
});

export default Application;
